import { ChangeDetectorRef } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { EMPTY, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { SetControlRelationship } from '../types';

type AbstractControlChangeEvent = (opts: { onlySelf?: boolean }) => void;

export type BaseCVAHandler = (arg?: unknown) => void;

const validatorHandler = (
  parentControl: AbstractControl,
  childControl: AbstractControl,
): ValidationErrors | null => {
  let errors: ValidationErrors | null = null;
  childControl.updateValueAndValidity({ emitEvent: false, onlySelf: true });
  const childErrors = childControl.errors;
  const parentErrors = parentControl.errors;
  if (Object.keys({ ...childErrors, ...parentErrors }).length > 0) {
    errors = { ...childErrors, ...parentErrors };
  }

  return errors;
};

export const markForCheck = (changeDetectorRef?: ChangeDetectorRef): void => {
  if (changeDetectorRef) changeDetectorRef.markForCheck();
};

/** Define the relationship between the subordinate and principal form controls.
 *
 * Dirty and pristine status, as well as errors of the principal will propagate
 * to the subordinate control.
 *
 * Optionally, the subordinate control can also write
 * to the principal, inherit its validators, or emit events on changes. */
export const setControlRelationship: SetControlRelationship = ({
  controlDirective,
  formControl,
  destroy$,
  onChange,
  valueTransform,
  changeDetectorRef,
  writeValueToParent = false,
  inheritValidators = false,
  emitEvent = false,
}) => {
  const parentControl = controlDirective?.control;

  if (!parentControl) return;

  if (controlDirective.dirty) {
    formControl.markAsDirty();
  }
  if (controlDirective.touched) {
    formControl.markAsTouched();
  }
  if (controlDirective.pristine) {
    formControl.markAsPristine();
  }
  if (controlDirective.pending) {
    formControl.markAsPending();
  }
  if (controlDirective.untouched) {
    formControl.markAsUntouched();
  }

  const markAsDirty = parentControl.markAsDirty.bind(
    parentControl,
  ) as AbstractControlChangeEvent;
  const markAsPristine = parentControl.markAsPristine.bind(
    parentControl,
  ) as AbstractControlChangeEvent;
  const markAsTouched = parentControl.markAsTouched.bind(
    parentControl,
  ) as AbstractControlChangeEvent;
  const markAllAsTouched = parentControl.markAllAsTouched.bind(
    parentControl,
  ) as () => void;
  const markAsUntouched = parentControl.markAsUntouched.bind(
    parentControl,
  ) as AbstractControlChangeEvent;
  const childMarkAsTouched = formControl.markAsTouched.bind(
    formControl,
  ) as AbstractControlChangeEvent;
  const childMarkAllAsTouched = formControl.markAllAsTouched.bind(
    formControl,
  ) as () => void;
  const childMarkAsUntouched = formControl.markAsUntouched.bind(
    formControl,
  ) as AbstractControlChangeEvent;
  const childMarkAsDirty = formControl.markAsDirty.bind(
    formControl,
  ) as AbstractControlChangeEvent;

  parentControl.markAsTouched = (options = {}) => {
    markAsTouched(options);
    if (formControl.untouched) formControl.markAsTouched(options);
    markForCheck(changeDetectorRef);
  };
  parentControl.markAsUntouched = (options = {}) => {
    markAsUntouched(options);
    if (formControl.touched) formControl.markAsUntouched(options);
    markForCheck(changeDetectorRef);
  };

  parentControl.markAsDirty = (options = {}) => {
    markAsDirty(options);
    if (formControl.pristine) formControl.markAsDirty();
    markForCheck(changeDetectorRef);
  };
  parentControl.markAsPristine = (options = {}) => {
    markAsPristine(options);
    if (formControl.dirty) formControl.markAsPristine();
    markForCheck(changeDetectorRef);
  };
  parentControl.markAllAsTouched = () => {
    markAllAsTouched();
    if (formControl.untouched) formControl.markAllAsTouched();
    markForCheck(changeDetectorRef);
  };
  formControl.markAsTouched = (options = {}) => {
    childMarkAsTouched(options);
    if (parentControl.untouched) parentControl.markAsTouched(options);
    markForCheck(changeDetectorRef);
  };
  formControl.markAsUntouched = (options = {}) => {
    childMarkAsUntouched(options);
    if (parentControl.touched) parentControl.markAsUntouched(options);
    markForCheck(changeDetectorRef);
  };

  formControl.markAsDirty = (options = {}) => {
    childMarkAsDirty(options);
    if (writeValueToParent && parentControl.pristine) {
      parentControl.markAsDirty(options);
    }
    markForCheck(changeDetectorRef);
  };

  formControl.markAllAsTouched = () => {
    childMarkAllAsTouched();
    if (parentControl.untouched) parentControl.markAllAsTouched();
    markForCheck(changeDetectorRef);
  };

  if (writeValueToParent) {
    formControl.valueChanges
      .pipe(takeUntil(destroy$))
      .subscribe((value: unknown) => {
        value = valueTransform ? valueTransform(value) : value;
        if (JSON.stringify(value) !== JSON.stringify(parentControl.value)) {
          onChange?.(value);
          markForCheck(changeDetectorRef);
        }
      });
  }

  if (inheritValidators) {
    const parentValidators = parentControl.validator;
    const parentAsyncValidators = parentControl.asyncValidator;
    if (parentValidators) {
      formControl.addValidators(parentValidators);
    }
    if (parentAsyncValidators) {
      formControl.addAsyncValidators(parentAsyncValidators);
    }
    formControl.updateValueAndValidity({ emitEvent, onlySelf: true });
  }

  // Update subordinate control, if parent was changed
  controlDirective.valueChanges?.pipe(takeUntil(destroy$)).subscribe(() => {
    const value = controlDirective.value as unknown;
    if (
      writeValueToParent &&
      JSON.stringify(value) !== JSON.stringify(formControl.value)
    ) {
      formControl.patchValue(value, { emitEvent });
      const errors = validatorHandler(parentControl, formControl);
      formControl.setErrors(errors, { emitEvent });
      markForCheck(changeDetectorRef);
    }
  });

  // Error passing.
  merge(controlDirective.statusChanges ?? EMPTY, formControl.valueChanges)
    .pipe(takeUntil(destroy$))
    .subscribe(() => {
      const errors = validatorHandler(parentControl, formControl);
      formControl.setErrors(errors, {
        emitEvent,
      });
      markForCheck(changeDetectorRef);
    });
};
